import {
  Invitation, Participant, Preference, peaksFor,
} from '../../invitations/models/invitation.model';

/** One eligible person, with the org and invitation they came from. */
export interface PoolRow {
  participant: Participant;
  orgId: string;
  orgName: string;
  invitationId: string;
  invitationName: string;
}

export interface Composition {
  /** People selected. */
  count: number;
  /** How many selected people PEAK in each preference. Sums above `count` — see below. */
  peaks: Record<Preference, number>;
  /** Preferences nobody peaks in. Empty when nothing is selected. */
  missing: Preference[];
  byOrg: Array<{ orgId: string; orgName: string; count: number }>;
}

const PREFERENCES: Preference[] = ['cl', 'id', 'dv', 'im'];

/**
 * Every eligible participant across the chosen organizations.
 *
 * `eligibleOf` is a PARAMETER on purpose: the eligibility rule (scored AND unlocked) is defined
 * once, in InvitationMockService.eligibleParticipants, and callers pass it in. Re-deriving the
 * predicate here would fork the definition of who may appear in a report — the exact trap the
 * spec calls out.
 *
 * An invitation belongs to exactly one org, so nobody can appear twice and no de-duplication is
 * needed.
 */
export function composePool(
  invitations: Invitation[],
  orgIds: string[],
  eligibleOf: (inv: Invitation) => Participant[],
): PoolRow[] {
  const wanted = new Set(orgIds);
  const rows: PoolRow[] = [];
  for (const inv of invitations) {
    if (!wanted.has(inv.rosterId)) { continue; }
    for (const participant of eligibleOf(inv)) {
      rows.push({
        participant,
        orgId: inv.rosterId, orgName: inv.rosterName,
        invitationId: inv.id, invitationName: inv.name,
      });
    }
  }
  return rows;
}

/**
 * The selected group's shape.
 *
 * The bars count PEAKS, not people: a two-peak Early Bird contributes to both Clarify and Ideate,
 * so the bars total more than the headcount. That is the FourSight model — the profile type IS the
 * peak set — not a bug, and the rail must say so or the numbers read as broken.
 */
export function compositionOf(rows: PoolRow[], selectedIds: string[]): Composition {
  const selected = new Set(selectedIds);
  // Drive off the pool, not off selectedIds: the picker can hold a selection while an org chip is
  // removed from under it, and a stale id must not be counted.
  const chosen = rows.filter(r => selected.has(r.participant.id));

  const peaks: Record<Preference, number> = { cl: 0, id: 0, dv: 0, im: 0 };
  for (const row of chosen) {
    for (const p of peaksFor(row.participant.profileTypeId)) { peaks[p]++; }
  }

  const byOrg: Composition['byOrg'] = [];
  for (const row of chosen) {
    const found = byOrg.find(o => o.orgId === row.orgId);
    if (found) { found.count++; }
    else { byOrg.push({ orgId: row.orgId, orgName: row.orgName, count: 1 }); }
  }

  return {
    count: chosen.length,
    peaks,
    // An empty report isn't "missing" all four — that would fire the warning before any work.
    missing: chosen.length ? PREFERENCES.filter(p => peaks[p] === 0) : [],
    byOrg,
  };
}
